<template>
	<div class="main-container" :class="{ open, closed: !open }">
		<div class="tab-container">
			<div
				class="tab"
				:class="{ active: open && showMessages }"
				@click="setActiveTab(tabs.MESSAGES)"
				@keyup.enter="setActiveTab(tabs.MESSAGES)"
				:aria-label="$t('meetings_chat_title')"
				tabindex="0"
				role="button"
			>
				{{ $t('meetings_chat_title') }}
				<span
					class="unread-count"
					v-if="unread !== 0 && (!open || !showMessages)"
					>({{ unread }})</span
				>
			</div>
			<div
				class="tab"
				:class="{ active: open && showParticipants }"
				@click="setActiveTab(tabs.PARTICIPANTS)"
				@keyup.enter="setActiveTab(tabs.PARTICIPANTS)"
				:aria-label="$t('meetings_participants_title')"
				tabindex="0"
				role="button"
			>
				{{ $t('meetings_participants_title') }}
			</div>
			<div
				class="tab-caret"
				:title="buttonTitle"
				@click="toggleOpen"
				@keyup.enter="toggleOpen"
				tabindex="0"
				role="button"
			>
				<img
					class="arrow"
					src="/images/pronto/up-arrow.svg"
					:class="{ flip: !open }"
				/>
			</div>
		</div>
		<div
			class="tab-content"
			:class="{
				open,
				closed: !open,
				'show-giphy': showGiphy,
			}"
		>
			<Messages
				v-show="showMessages"
				:showing="showMessages && open"
				@unread="updateUnread"
			/>
			<Participants v-if="showParticipants" />
		</div>
	</div>
</template>

<script>
import Messages from './Messages'
import Participants from './Participants'
import { getHostUser, getCurrentUser } from '../data'

const tabs = {
	MESSAGES: 'messages',
	PARTICIPANTS: 'participants',
}

export default {
	name: 'SidebarTabs',
	components: { Messages, Participants },
	props: {
		open: {
			type: Boolean,
			default: null,
		},
	},

	data() {
		return {
			tabs,
			selectedTab: tabs.PARTICIPANTS,
			showGiphy: false,
			unread: 0,
		}
	},

	emits: ['set-open'],

	mounted() {
		const host = getHostUser()
		const { id } = getCurrentUser()
		if (host.id !== id) {
			this.selectedTab = tabs.MESSAGES
		}

		EventBus.$on('showGiphy', (open) => {
			this.showGiphy = open
		})
	},

	methods: {
		setActiveTab(tab) {
			this.selectedTab = tab
			this.$emit('set-open', true)
		},

		toggleOpen() {
			this.$emit('set-open', !this.open)
		},

		updateUnread(data) {
			this.unread = data
		},
	},

	computed: {
		showMessages() {
			return this.selectedTab === tabs.MESSAGES
		},
		showParticipants() {
			return this.selectedTab === tabs.PARTICIPANTS
		},
		buttonTitle() {
			return this.$t(`common_button_${this.open ? 'collapse' : 'expand'}`)
		},
	},
}
</script>

<style lang="scss" scoped>
@import 'src/sass/variables';

$gap: 16px;
$sidebarWidth: 300px;
$transition-time: 250ms;
$transition-slide-time: 150ms;
$transition-delay: 200ms;

.main-container {
	background: #222;
	position: relative;
	transition: border-radius linear $transition-slide-time;
	&.open {
		border-radius: 10px 10px 0 0;
	}
	&.open {
		border-radius: 10px 10px 0 0;
		transition-delay: $transition-delay;
	}
	&.closed {
		border-radius: 10px;
	}
}
.tab-container {
	cursor: pointer;
	display: flex;
	align-items: center;
	width: $sidebarWidth;
}

.tab {
	padding: 8px $gap;
	color: white;
	flex: 1;
	font-size: 14px;
	text-align: center;
	&.active {
		color: var(--teal-300);
	}
	outline: none;
}

.unread-count {
	font-size: 12px;
	color: var(--teal-300);
}

.tab-caret {
	padding: 6px $gap;
	border-left: 1px solid #313131;
	outline: none;
}

.tab-caret .arrow {
	width: 12px;
	padding-bottom: 2px;
}

.flip {
	transform: rotate(180deg);
}

.tab-content {
	height: 0;
	overflow: hidden;
	transition: all ease-in $transition-slide-time;
	transition-property: height, border-color;
	background: #222;
	width: $sidebarWidth;
	position: absolute;
	z-index: 5;
	border-radius: 0 0 10px 10px;
	border-top: 1px solid transparent;
	&.open {
		transition-delay: $transition-delay;
		height: calc(100vh - 67px);
		border-color: #313131;
	}
	&.closed {
		height: 0;
		border-top: transparent;
	}
	&.show-giphy {
		// Safari bug: fixed position items requires
		// that we make this container overflow visible whenever
		// these elements are open
		overflow: visible !important;
	}
}
</style>
